import React, { useState, useCallback, useEffect } from 'react';
import {
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { DateField } from '../../../../../Components/Field';
import { StepProps } from './types';
import { StepButtonBar } from './StepButtonBar';
import OtherRequirementInfo from './OtherRequirementInfo';

interface Step6EffectiveDatesProps extends StepProps {
  otherRequirementInfo: any;
  setOtherRequirementInfo: (any) => void;
}

const Step6EffectiveDates: React.FC<Step6EffectiveDatesProps> = ({
  currentStepName,
  onNext,
  onBack,
  onCancel,
  currentData,
  isFirstStep,
  otherRequirementInfo,
  setOtherRequirementInfo,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const paperPadding = isMobile ? theme.spacing(3) : theme.spacing(8);

  const [effectiveFrom, setEffectiveFrom] = useState<Date | undefined>(
    currentData.effectiveFrom ? new Date(currentData.effectiveFrom) : undefined
  );
  const [effectiveTo, setEffectiveTo] = useState<Date | undefined>(
    currentData.effectiveTo ? new Date(currentData.effectiveTo) : undefined
  );

  useEffect(() => {
    if (currentData.effectiveFrom) {
      setEffectiveFrom(new Date(currentData.effectiveFrom));
    }
    if (currentData.effectiveTo) {
      setEffectiveTo(new Date(currentData.effectiveTo));
    }
  }, [currentData.effectiveFrom, currentData.effectiveTo]);

  const handleEffectiveFromChange = useCallback((val: any) => {
    if (val && typeof val === 'object' && 'value' in val) {
      setEffectiveFrom(val.value);
    } else if (val === undefined || val === null) {
      setEffectiveFrom(undefined);
    }
  }, []);

  const handleEffectiveToChange = useCallback((val: any) => {
    if (val && typeof val === 'object' && 'value' in val) {
      setEffectiveTo(val.value);
    } else if (val === undefined || val === null) {
      setEffectiveTo(undefined);
    }
  }, []);

  const handleNext = () => {
    if (effectiveFrom) {
      onNext({
        effectiveFrom,
        effectiveTo: effectiveTo || undefined,
      });
    }
  };

  const isValid = !!effectiveFrom;
  const fromDateError = !effectiveFrom;

  return (
    <Box style={{ width: '100%' }}>
      <Box
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <Box
          style={{
            width: isMobile ? '100%' : 'calc(50% - 5px)',
            flexShrink: 0,
            padding: paperPadding,
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <Box style={{ maxWidth: '320px', width: '100%', marginBottom: theme.spacing(3) }}>
            <DateField
              name="effective-from"
              placeholder={formatMessage({ id: 'law-portal.manage.requirements.effectiveFrom', defaultMessage: 'Effective From' })}
              errorTexts={[]}
              initialState={effectiveFrom ? { value: effectiveFrom } : undefined}
              onChange={handleEffectiveFromChange}
            />
          </Box>

          <Box style={{ maxWidth: '320px', width: '100%' }}>
            <DateField
              name="effective-to"
              placeholder={formatMessage({ id: 'law-portal.manage.requirements.effectiveTo', defaultMessage: 'Effective To' })}
              errorTexts={[]}
              initialState={effectiveTo ? { value: effectiveTo } : undefined}
              onChange={handleEffectiveToChange}
            />
          </Box>
        </Box>

        <OtherRequirementInfo otherRequirementStepInfo={otherRequirementInfo} currentStepName={currentStepName} onUpdate={setOtherRequirementInfo} />
      </Box>

      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleNext}
        nextLabel="form.next"
        nextDisabled={!isValid}
        formatMessage={formatMessage}
        showBack={true}
      />
    </Box>
  );
};

export default Step6EffectiveDates;
